import { useQuery } from '@apollo/client'
import { useParams } from 'react-router'
import { ContentGrid } from '../../components/ContentGrid'
import {
  GET_CLIPS_BY_BRAND_ID,
  GET_CREATORS_BY_BRAND_WITH_CLIPS,
  GetClipsByBrandResponse,
  QueryGetCreatorsByBrandWithClipsResponse,
} from '../../gql'
import {
  GetClipsSort,
  QueryGetClipsByBrandIdArgs,
  QueryGetCreatorsByBrandWithClipsArgs,
  User,
} from '../../gql/types'
import { LIMIT_DEFAULT } from '../../utils/constants'
import { Header } from './components/Header'
import { useState, useMemo } from 'react'
import Multiselect from '../../elements/Multiselect'
import { ListOptions } from '../../components'
import { Avatar } from '../../elements'

interface IProps {
  user: User
}

export const Clips = ({ user }: IProps) => {
  const { id } = useParams()
  const limit = LIMIT_DEFAULT
  const [selectedCreators, setSelectedCreators] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [currentQuery, setCurrentQuery] = useState('')

  // Fetch creators with clips for this brand
  const { data: creatorsData } = useQuery<
    QueryGetCreatorsByBrandWithClipsResponse,
    QueryGetCreatorsByBrandWithClipsArgs
  >(GET_CREATORS_BY_BRAND_WITH_CLIPS, {
    variables: {
      brandId: +(id as string),
    },
  })

  // Create creator options for multiselect
  const creatorOptions = useMemo(() => {
    if (!creatorsData?.getCreatorsByBrandWithClips) return []

    return [...creatorsData.getCreatorsByBrandWithClips]
      .sort((a, b) => a.firstName.localeCompare(b.firstName))
      .map((creator) => ({
        value: creator.id.toString(),
        label: creator.firstName,
        avatar: (
          <Avatar
            type='user'
            size='xxs'
            src={creator.avatar?.url}
            alt={creator.firstName}
          />
        ),
      }))
  }, [creatorsData])

  // Create creatorIds array for filtering
  const creatorIds = useMemo(() => {
    return selectedCreators.map((creator) => +creator.value)
  }, [selectedCreators])

  // Query for clips with current filters
  const { loading, error, data, fetchMore, refetch } = useQuery<
    GetClipsByBrandResponse,
    QueryGetClipsByBrandIdArgs
  >(GET_CLIPS_BY_BRAND_ID, {
    variables: {
      brandId: +(id as string),
      options: {
        query: currentQuery,
        filters: {
          creatorIds: creatorIds.length > 0 ? creatorIds : undefined,
        },
        sort: GetClipsSort.newest,
        limit,
        skip: 0,
      },
    },
  })

  const fetchData = () => {
    if (loading) {
      return
    }

    // if we have all the data, don't fetch more
    if (
      data?.getClipsByBrandId?.items?.length >=
      data?.getClipsByBrandId?.pagination?.numberOfHits
    ) {
      return
    }

    fetchMore({
      variables: {
        brandId: +(id as string),
        options: {
          query: currentQuery,
          filters: {
            creatorIds: creatorIds.length > 0 ? creatorIds : undefined,
          },
          sort: GetClipsSort.newest,
          limit,
          skip: data?.getClipsByBrandId?.items?.length ?? 0,
        },
      },
    })
  }

  const onSearch = () => {
    setCurrentQuery(searchQuery)
    refetch({
      brandId: +(id as string),
      options: {
        query: searchQuery,
        filters: {
          creatorIds: creatorIds.length > 0 ? creatorIds : undefined,
        },
        sort: GetClipsSort.newest,
        limit,
        skip: 0,
      },
    })
  }

  // Handle creator selection change
  const handleCreatorChange = (newSelection: any[] | null) => {
    setSelectedCreators(newSelection || [])
    refetch({
      brandId: +(id as string),
      options: {
        query: currentQuery,
        filters: {
          creatorIds:
            newSelection && newSelection.length > 0
              ? newSelection.map((creator) => +creator.value)
              : undefined,
        },
        sort: GetClipsSort.newest,
        limit,
        skip: 0,
      },
    })
  }

  if (error) {
    console.log(error)
    return <p>There has been an error. Try refreshing the page</p>
  }

  return (
    <div>
      <Header />
      <ListOptions
        query={searchQuery}
        setQuery={setSearchQuery}
        onSearch={onSearch}
        searchPlaceholder='Search by campaign or creator name...'
        rightComponent={
          <Multiselect
            options={creatorOptions}
            selected={selectedCreators}
            onChange={handleCreatorChange}
            placeholder='Select creators...'
            customText={
              selectedCreators.length > 0
                ? `${selectedCreators.length} creators selected`
                : ''
            }
            className='w-full md:w-[400px] rounded-lg shadow-none text-gray-500 text-sm'
            maxMenuHeight={400}
            displayTags={true}
            maxTagDisplay={3}
          />
        }
        searchOnLeft={true}
        className='border-b-0 !bg-transparent'
      />

      <ContentGrid
        clips={data?.getClipsByBrandId?.items || []}
        hasMore={
          data?.getClipsByBrandId?.items?.length <
          data?.getClipsByBrandId?.pagination?.numberOfHits
        }
        fetchData={fetchData}
        loading={loading}
        allowDownload={true}
        type='brand'
      />
    </div>
  )
}
